import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  BuildOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import { getSelectedProductionTerminology } from "./productionTerminology";
import {
  getProductionEquipment,
  createProductionEquipment,
  updateProductionEquipment,
  deleteProductionEquipment,
} from "./api";

const { Text } = Typography;

const EQUIPMENT_MANAGE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Materiels.manage";

const formatCurrency = (value) =>
  `${Math.round(Number(value) || 0).toLocaleString("fr-FR")} CFA`;

const formatRate = (value) => {
  const numeric = Number(value) || 0;
  const rounded = Math.round(numeric * 10000) / 10000;
  return `${rounded.toLocaleString("fr-FR", {
    maximumFractionDigits: 4,
  })} CFA / u`;
};

const STATUS_TAGS = {
  active: { color: "green", label: "Actif" },
  fully_depreciated: { color: "gold", label: "Totalement amorti" },
  archived: { color: "default", label: "Archivé" },
};

// Calcul du taux (unites d'oeuvre) cote client, pour l'apercu en direct.
// Doit rester coherent avec computeEquipmentDepreciation cote backend.
const computeRatePreview = (values) => {
  const purchaseValue = Math.max(Number(values?.purchase_value) || 0, 0);
  const residualValue = Math.max(Number(values?.residual_value) || 0, 0);
  const base = Math.max(purchaseValue - residualValue, 0);

  const basis =
    values?.depreciation_basis === "time_capacity"
      ? "time_capacity"
      : "output_units";

  let lifetime = 0;
  if (basis === "time_capacity") {
    lifetime =
      Math.max(Number(values?.useful_life_months) || 0, 0) *
      Math.max(Number(values?.expected_output_per_month) || 0, 0);
  } else {
    lifetime = Math.max(Number(values?.lifetime_output_units) || 0, 0);
  }

  return {
    base,
    lifetime,
    rate: lifetime > 0 ? base / lifetime : 0,
  };
};

const ProductionEquipmentList = () => {
  const { hasPermission, user } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const productionTerminology = getSelectedProductionTerminology(
    user,
    selectedCompany
  );
  const [form] = Form.useForm();

  const canManage = hasPermission(EQUIPMENT_MANAGE_PERMISSION);

  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Champs surveilles pour l'apercu du taux (reagit aux saisies ET a setFieldsValue).
  const wPurchase = Form.useWatch("purchase_value", form);
  const wResidual = Form.useWatch("residual_value", form);
  const watchedBasis = Form.useWatch("depreciation_basis", form);
  const wLifetime = Form.useWatch("lifetime_output_units", form);
  const wMonths = Form.useWatch("useful_life_months", form);
  const wPerMonth = Form.useWatch("expected_output_per_month", form);
  const ratePreview = useMemo(
    () =>
      computeRatePreview({
        purchase_value: wPurchase,
        residual_value: wResidual,
        depreciation_basis: watchedBasis,
        lifetime_output_units: wLifetime,
        useful_life_months: wMonths,
        expected_output_per_month: wPerMonth,
      }),
    [wPurchase, wResidual, watchedBasis, wLifetime, wMonths, wPerMonth]
  );

  const loadEquipment = useCallback(async () => {
    if (!selectedWarehouse) {
      setEquipment([]);
      return;
    }
    setLoading(true);
    try {
      const response = await getProductionEquipment({
        warehouse_id: selectedWarehouse,
        include_archived: "1",
      });
      setEquipment(
        Array.isArray(response?.equipment) ? response.equipment : []
      );
    } catch (error) {
      message.error(
        error?.error || "Erreur lors du chargement des matériels."
      );
      setEquipment([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse]);

  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    form.setFieldsValue({
      depreciation_basis: "output_units",
      residual_value: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      reference: record.reference || "",
      purchase_value: Number(record.purchase_value) || 0,
      residual_value: Number(record.residual_value) || 0,
      acquisition_date: record.acquisition_date
        ? String(record.acquisition_date).slice(0, 10)
        : "",
      depreciation_basis:
        record.depreciation_basis === "time_capacity"
          ? "time_capacity"
          : "output_units",
      lifetime_output_units: Number(record.lifetime_output_units) || null,
      useful_life_months: record.useful_life_months || null,
      expected_output_per_month:
        Number(record.expected_output_per_month) || null,
      notes: record.notes || "",
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    if (!selectedWarehouse) {
      message.error("Veuillez sélectionner un établissement.");
      return;
    }

    const payload = {
      warehouse_id: selectedWarehouse,
      name: values.name,
      reference: values.reference || null,
      purchase_value: Number(values.purchase_value) || 0,
      residual_value: Number(values.residual_value) || 0,
      acquisition_date: values.acquisition_date || null,
      depreciation_basis: values.depreciation_basis,
      lifetime_output_units:
        values.depreciation_basis === "output_units"
          ? Number(values.lifetime_output_units) || 0
          : null,
      useful_life_months:
        values.depreciation_basis === "time_capacity"
          ? Number(values.useful_life_months) || 0
          : null,
      expected_output_per_month:
        values.depreciation_basis === "time_capacity"
          ? Number(values.expected_output_per_month) || 0
          : null,
      notes: values.notes || null,
    };

    setSubmitting(true);
    try {
      if (editingId) {
        await updateProductionEquipment(editingId, payload);
        message.success("Matériel mis à jour.");
      } else {
        await createProductionEquipment(payload);
        message.success("Matériel enregistré.");
      }
      setModalOpen(false);
      await loadEquipment();
    } catch (error) {
      message.error(
        error?.error || "Erreur lors de l'enregistrement du matériel."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const response = await deleteProductionEquipment(record.id, {
        warehouse_id: selectedWarehouse,
      });
      if (response?.archived) {
        message.success(
          `Matériel archivé (déjà utilisé dans des ${productionTerminology.processPlural}, retiré des ${productionTerminology.configurationPlural}).`
        );
      } else {
        message.success("Matériel supprimé.");
      }
      await loadEquipment();
    } catch (error) {
      message.error(
        error?.error || "Erreur lors de la suppression du matériel."
      );
    }
  };

  const columns = [
    {
      title: "Matériel",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          {record.reference ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Réf. {record.reference}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Valeur d'achat",
      dataIndex: "purchase_value",
      key: "purchase_value",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Taux / unité",
      dataIndex: "depreciation_rate_per_unit",
      key: "depreciation_rate_per_unit",
      align: "right",
      render: (value) => formatRate(value),
    },
    {
      title: "Amorti / base",
      key: "progress",
      align: "right",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{formatCurrency(record.accumulated_depreciation)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            / {formatCurrency(record.depreciable_base)}
          </Text>
        </Space>
      ),
    },
    {
      title: "Reste à amortir",
      dataIndex: "remaining_depreciation",
      key: "remaining_depreciation",
      align: "right",
      render: (value) => formatCurrency(value),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value) => {
        const tag = STATUS_TAGS[value] || STATUS_TAGS.active;
        return <Tag color={tag.color}>{tag.label}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) =>
        canManage ? (
          <Space>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
            <Popconfirm
              title="Supprimer ce matériel ?"
              description="S'il est déjà utilisé en production, il sera archivé."
              okText="Confirmer"
              cancelText="Annuler"
              onConfirm={() => handleDelete(record)}
            >
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  if (!selectedWarehouse) {
    return (
      <Alert
        type="info"
        showIcon
        style={{ margin: 16 }}
        message="Sélectionnez un établissement"
        description="Choisissez un établissement pour gérer ses matériels de production."
      />
    );
  }

  return (
    <Card
      style={{ marginTop: 12 }}
      title={
        <span>
          <BuildOutlined style={{ marginRight: 8 }} />
          Matériels & amortissements
        </span>
      }
      extra={
        canManage ? (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreate}
          >
            Ajouter un matériel
          </Button>
        ) : null
      }
    >
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        Enregistrez vos machines et équipements avec leur valeur d'achat et leur
        durée de vie estimée. L'amortissement, calculé en unités d'œuvre, est
        intégré au coût de revient des {productionTerminology.processPlural} via les {productionTerminology.configurationPlural} qui utilisent
        ce matériel.
      </Text>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={equipment}
        loading={loading}
        pagination={false}
        size="middle"
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "Aucun matériel enregistré." }}
      />

      <Modal
        title={editingId ? "Modifier le matériel" : "Nouveau matériel"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Nom du matériel"
            rules={[{ required: true, message: "Le nom est requis." }]}
          >
            <Input placeholder="Ex: Pétrin, Four, Presse..." />
          </Form.Item>

          <Form.Item name="reference" label="Référence (optionnel)">
            <Input placeholder="Ex: N° de série, modèle..." />
          </Form.Item>

          <Space size="large" style={{ display: "flex" }} align="start">
            <Form.Item
              name="purchase_value"
              label="Valeur d'achat (CFA)"
              rules={[
                { required: true, message: "La valeur d'achat est requise." },
                {
                  validator: (_, value) =>
                    Number(value) > 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error("La valeur d'achat doit être > 0.")
                        ),
                },
              ]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
              name="residual_value"
              label="Valeur résiduelle (CFA)"
              tooltip="Valeur estimée en fin de vie (0 par défaut)."
              style={{ flex: 1 }}
            >
              <InputNumber min={0} step={1000} style={{ width: "100%" }} />
            </Form.Item>
          </Space>

          <Form.Item name="acquisition_date" label="Date d'acquisition (optionnel)">
            <Input type="date" />
          </Form.Item>

          <Form.Item
            name="depreciation_basis"
            label="Base de calcul de l'amortissement"
          >
            <Select
              options={[
                {
                  value: "output_units",
                  label: "Par volume total produit (unités d'œuvre)",
                },
                {
                  value: "time_capacity",
                  label: "Par durée (mois) + capacité mensuelle",
                },
              ]}
            />
          </Form.Item>

          {watchedBasis === "time_capacity" ? (
            <Space size="large" style={{ display: "flex" }} align="start">
              <Form.Item
                name="useful_life_months"
                label="Durée de vie (mois)"
                rules={[
                  { required: true, message: "La durée est requise." },
                ]}
                style={{ flex: 1 }}
              >
                <InputNumber min={1} step={1} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="expected_output_per_month"
                label="Capacité estimée / mois (unités)"
                rules={[
                  { required: true, message: "La capacité est requise." },
                ]}
                style={{ flex: 1 }}
              >
                <InputNumber min={1} step={1} style={{ width: "100%" }} />
              </Form.Item>
            </Space>
          ) : (
            <Form.Item
              name="lifetime_output_units"
              label="Volume total estimé sur la durée de vie (unités)"
              tooltip="Nombre total d'unités que ce matériel produira sur sa durée de vie."
              rules={[
                { required: true, message: "Le volume total est requis." },
              ]}
            >
              <InputNumber min={1} step={100} style={{ width: "100%" }} />
            </Form.Item>
          )}

          <Alert
            type={ratePreview.rate > 0 ? "success" : "warning"}
            showIcon
            style={{ marginBottom: 16 }}
            message={
              ratePreview.rate > 0
                ? `Taux d'amortissement : ${formatRate(ratePreview.rate)}`
                : "Renseignez la valeur d'achat et le volume/durée pour calculer le taux."
            }
            description={
              ratePreview.rate > 0
                ? `Base amortissable ${formatCurrency(
                    ratePreview.base
                  )} répartie sur ${ratePreview.lifetime.toLocaleString(
                    "fr-FR"
                  )} unités d'œuvre.`
                : null
            }
          />

          <Form.Item name="notes" label="Notes (optionnel)">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ProductionEquipmentList;
